import { CurrencyPipe } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormatPercentPipe } from '../../../pipes/FormatPercentPipe';
import { Store } from '@ngrx/store';
import { ReviewPageActions } from 'projects/money-movement-ui/src/app/create-request/actions';
import { CheckTransactionDetails, ChecksExtraPrompts, TransactionForm, isCheckTransactionForm } from 'projects/money-movement-ui/src/app/create-request/models/form/TransactionForm';
import { CodeAndDescription } from 'projects/money-movement-ui/src/app/create-request/models/shared/CodeAndDescription';
import { selectCheckInfo, selectCheckReasonMap, selectStagedTransactionForm } from 'projects/money-movement-ui/src/app/create-request/reducers';
import { selectFilteredChecksStandingAuth, selectIsPendingCheckSloaSetup, selectIsPtrs, selectNraIndicator, selectStateCode } from 'projects/money-movement-ui/src/app/account-info-header/reducers';
import { combineLatest, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SentenceCasePipe } from '../../../pipes/sentence-case.pipe';
import { checkPayeeDict, CheckPayeeTypes, checkRecipientDict, CheckRecipientTypes, Constants, frequencyDict, FrequencyTypes, transactionDetailLabels, transactionTypes, transactionTypesDict, payIncomeDict, PayIncomeType, amountTypeDict, pdfLabelsSubmitDetails, yesNo } from '../../../utilities/Constants';
import { AmountType } from "../../../types/Transaction";
import { selectIsIncoming, selectIsStandingAuthViewPage, selectIsTransactionsViewPage } from 'projects/money-movement-ui/src/app/transaction-view/reducers';
import { selectViewInContext } from '../../../reducers';
import { CheckInfo } from 'projects/money-movement-ui/src/app/create-request/models/check/CheckInfo';
import { selectInitReleaseTransactionsByDraftIdCallSuccess } from '../../../../initiate-and-release/reducers';
import { FormatDatePipe } from '../../../pipes/FormatDatePipe';
import { StandingAuthDetails } from 'projects/money-movement-ui/src/app/shared/types/StandingInstruction';

@Component({
  standalone: false,
  selector: 'mm-transaction-details-section-check',
  templateUrl: './transaction-details-section-check.component.html',
  styleUrls: ['./transaction-details-section-check.component.scss']
})
export class TransactionDetailsSectionCheckComponent implements OnInit {
  @Input() isInrFromTable: boolean;
  readonly labels = transactionDetailLabels;
  readonly pdfLabelsSubmitDetails = pdfLabelsSubmitDetails;
  readonly processDateCopy = Constants.checkProcessDateReviewCopy;
  readonly taxCopyParagraphOne: string = Constants.taxCopyParagraphOne;
  readonly taxCopyParagraphTwo: string = Constants.taxCopyParagraphTwo;
  amount: string;
  checkFee: number;
  checkReason: string;
  currencyPipe = new CurrencyPipe('en-US', 'USD');
  deliveryType: string;
  memo: string;
  createRequestStep$: Observable<string>;
  deliveryAddress: string[];
  deliveryMethod: string;
  destroy$: Subject<boolean> = new Subject<boolean>();
  frequency: string;
  payee: CheckPayeeTypes;
  payeeName: string[];
  processDate: string;
  sentenceCasePipe = new SentenceCasePipe;
  transactionTypeLabel: string = transactionTypesDict[transactionTypes.check];
  useStandingAuth: string;
  checkReasonMap: CodeAndDescription[];
  recipient: CheckRecipientTypes;
  frequencyDescription: string;
  secondProcessDate: string;
  endDate: string;
  isRecurring: boolean;
  amountType: string;
  fedTaxPercent: string;
  stateTaxPercent: string;
  fedTaxAmount: string;
  stateTaxAmount: string;
  distributionReason: string;
  netAmount: string;
  totalWithdrawalAmount: string;
  iraAccount: boolean;
  hasSecondProcessDate: boolean;
  hasEndDate: boolean;
  payIncome: PayIncomeType;
  isLastBiz: boolean = false;
  isPtrs: boolean;
  trustIncomePercent: string;
  trustPrincipalPercent: string;
  trustTaxCode: string;
  trustPaidFor: string;
  trustComment: string;
  trustTaxCategory: string;
  standingAuthDetails: StandingAuthDetails | null = null;
  instructionId: string;
  isStandingAuthViewPage: boolean;
  isTransactionViewPage: boolean;
  nextProcessDate: string;
  viewPagePtrsInfoBannerPrimaryText = Constants.viewPagePtrsInfoBannerPrimaryText;
  viewPagePtrsInfoBannerBoldText = Constants.viewPagePtrsInfoBannerBoldText;
  isPendingSloaSetup: boolean;
  isIncoming: boolean;
  depositAmount: string;
  fundsHeld: string[];
  iraContributionType: string;
  asOfDate: string;
  isBsloaTransaction: boolean;
  checkNumber: string;
  transactionStatus: string;
  stateTaxLabel: string = this.labels.stateTax;
  memoLine2: string;
  closeAccount: string;
  isOneTimeTotalDist: boolean;
  reportsRequested: string[] = [];
  reportsRequestedTooltip: string = Constants.reportsRequestedTooltip;
  standingAuthNicknameInstructionId: string = '';


  constructor(private store: Store, private changeDetectionRef: ChangeDetectorRef, private dateFormatPipe: FormatDatePipe) { }

  ngOnInit(): void {
    this.store.select(selectIsStandingAuthViewPage).pipe(takeUntil(this.destroy$)).subscribe(value => this.isStandingAuthViewPage = value);
    this.store.select(selectIsTransactionsViewPage).pipe(takeUntil(this.destroy$)).subscribe(value => this.isTransactionViewPage = value);

    combineLatest([
      this.store.select(selectStagedTransactionForm),
      this.store.select(selectCheckInfo),
      this.store.select(selectInitReleaseTransactionsByDraftIdCallSuccess),
    ]).pipe(takeUntil(this.destroy$)).subscribe(([form, checkInfo, draftCallSuccess]) => {
      const formatPercent = new FormatPercentPipe;
      if (isCheckTransactionForm(form)) {
        let extraPrompts = form.extraPrompts;
        this.isBsloaTransaction = extraPrompts.useStandingAuth === 'bundled';
        let isTableEditFlow = form.prefilledFlow.name === 'edit-table' && form.prefilledFlow.state === 'complete';
        let transactionDetails = form.transactionDetails;
        this.closeAccount = form?.extraPrompts?.closeAccount ? form?.extraPrompts?.closeAccount?.charAt(0).toUpperCase() + form?.extraPrompts?.closeAccount?.slice(1) : '';
        this.useStandingAuth = this.isBsloaTransaction ? extraPrompts?.useBundledStandingAuth === 'recurring' ? 'no' : extraPrompts?.useBundledStandingAuth! : extraPrompts?.useStandingAuth;
        this.frequency = this.getFrequency(extraPrompts);
        this.isOneTimeTotalDist = this.frequency === frequencyDict['onetimeTotalDist'];
        this.memo = transactionDetails.checkMemo ? transactionDetails.checkMemo.trim() : '';
        this.checkReason = transactionDetails.checkReason ? transactionDetails.checkReason.trim() : '';
        this.memoLine2 = transactionDetails.memoLine2 ? transactionDetails.memoLine2.trim() : '';
        this.deliveryMethod = transactionDetails.deliveryMethod ?? '';
        this.payee = this.getPayee(this.useStandingAuth, transactionDetails, checkInfo.isThirdParty);
        this.payeeName = this.useStandingAuth === yesNo.yes ? checkInfo.defaultPayee : this.getPayeeName(transactionDetails, form.transactionDetails.modifiedPayeeCheckbox ?? false, checkInfo);
        this.deliveryType = transactionDetails.recipient ?? '';
        this.recipient = checkRecipientDict[transactionDetails.recipient!] as CheckRecipientTypes;
         //SAC-385335 We do not receive the delivery address header label on the View page for Advisory Firm ..We should remove the header label from being shown on the View page
        let deliveryAddress = this.getDeliveryAddress(this.deliveryType, checkInfo.defaultRecipient, checkInfo.deliveryAddress, transactionDetails);
        this.deliveryAddress = this.isTransactionViewPage || this.isStandingAuthViewPage ? deliveryAddress : [this.recipient as string].concat(deliveryAddress); 
        this.checkFee = checkInfo.checkFee;
        this.processDate = checkInfo.processDate;
        this.amount = this.getAmountByAmountType(transactionDetails, checkInfo.totalWithdrawalAmount);
        this.secondProcessDate = transactionDetails.secondProcessDate ?? '';
        this.endDate = transactionDetails.endDate ?? '';
        this.frequencyDescription = transactionDetails.frequencyDescription ? transactionDetails.frequencyDescription : 
          checkInfo?.frequencyList ? checkInfo.frequencyList.find(frequency => frequency.code === transactionDetails.recurringFrequency)?.description! : '';
        this.isRecurring = (this.frequency === (frequencyDict['recurring'] as FrequencyTypes));
        this.totalWithdrawalAmount = this.isOneTimeTotalDist ? checkInfo?.totalWithdrawalAmount : checkInfo.amount ?? Constants.zeroAmount;
        this.amountType = transactionDetails.amountType!;
        this.fedTaxPercent = formatPercent.transform(checkInfo.fedTaxRate);
        this.stateTaxPercent = checkInfo.stateTaxRate != '0' ? formatPercent.transform(checkInfo.stateTaxRate) : formatPercent.transform('0');
        this.fedTaxAmount = checkInfo.fedTaxAmountFormatted ?? '0';
        this.stateTaxAmount = checkInfo.stateTaxAmountFormatted ?? '0';
        this.distributionReason = checkInfo.iraAccount ? transactionDetails?.taxWithholdingsFormGroup?.distributionReason! : "";
        this.iraAccount = checkInfo.iraAccount;
        this.hasSecondProcessDate = this.secondProcessDate ? true : false;
        this.hasEndDate = this.endDate ? true : false;
        this.payIncome = transactionDetails.recurringFrequency === 'LASTBIZ' && transactionDetails?.payIncome ? payIncomeDict[transactionDetails.payIncome] as PayIncomeType : '' as PayIncomeType;
        this.trustIncomePercent = transactionDetails!.trustIncomePercent ?? '';
        this.trustPrincipalPercent = transactionDetails!.trustPrincipalPercent ?? '';
        this.trustTaxCode = transactionDetails!.trustTaxCode ?? '';
        this.trustPaidFor = transactionDetails!.trustPaidFor ?? '';
        this.trustTaxCategory = transactionDetails.trustTaxCategory ?? '';
        this.instructionId = extraPrompts.standingAuthId?.includes('-') ? (extraPrompts.standingAuthId?.split('-')[1]) : extraPrompts.standingAuthId ? extraPrompts.standingAuthId : '';
        this.nextProcessDate = checkInfo.cashieringCheckInputDetails.nextProcessDate ?? '';
        this.trustComment = transactionDetails.trustComment ?? '';
        this.depositAmount = checkInfo.depositAmount ?? '';
        this.fundsHeld = checkInfo.fundsHeld ?? [];
        this.iraContributionType = checkInfo.contributionType ?? '';
        this.asOfDate = checkInfo.asOfDate ?? '';
        this.transactionTypeLabel = checkInfo.isBsloaTransaction && this.isStandingAuthViewPage ? 'Check - bundled' : this.getTransactionTypeLabel(extraPrompts);
        if ((this.isTransactionViewPage || isTableEditFlow || draftCallSuccess) && this.useStandingAuth === 'yes') {
          this.standingAuthDetails = {
            nickname: this.resolveStandingAuthNickname(checkInfo?.nickName),
            payeeLines: [
              checkInfo.payeeDetails.payeeName1,
              checkInfo.payeeDetails.payeeName2,
              checkInfo.payeeDetails.payeeName3
            ],
            addressLines: [
              `${checkInfo.recipient.addressLine1} ${checkInfo.recipient.addressLine2} ${checkInfo.recipient.city}, ${checkInfo.recipient.stateCode}`,
              `${checkInfo.recipient.zipCode} ${checkInfo.recipient.countryCode}`
            ]
          };
        }
        this.formatReportsRequestedFields(form);
        this.checkNumber = checkInfo.checkNumber?.toString() ?? '0';
        this.transactionStatus = checkInfo.status?.description ?? '';
        this.changeDetectionRef.detectChanges();
      }
    })

    this.store.select(selectIsPendingCheckSloaSetup).pipe(takeUntil(this.destroy$)).subscribe(isPendingSloaSetup => {
      this.isPendingSloaSetup = isPendingSloaSetup;
    })

    this.store.select(selectIsIncoming).pipe(takeUntil(this.destroy$)).subscribe(value => this.isIncoming = value);

    this.store.select(selectFilteredChecksStandingAuth).pipe(takeUntil(this.destroy$)).subscribe(instructions => {
      let selectedStandingAuthorization = instructions.find(auths => auths.cashieringRqstId === this.instructionId);
      if (selectedStandingAuthorization) {
        this.standingAuthDetails = {
          nickname: this.resolveStandingAuthNickname(selectedStandingAuthorization.nickname),
          payeeLines: [
            selectedStandingAuthorization.to1,
            selectedStandingAuthorization.to2,
            selectedStandingAuthorization.to3
          ],
          addressLines: [selectedStandingAuthorization.address]
        };
      }
    })

    this.store.select(selectCheckReasonMap).subscribe(checkReasonMap => {
      this.checkReasonMap = checkReasonMap;
    });

    this.store.select(selectIsPtrs).pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.isPtrs = value;
    });

    this.createRequestStep$ = this.store.select(selectViewInContext);

    combineLatest([
      this.store.select(selectStateCode),
      this.store.select(selectNraIndicator)
    ]).pipe(takeUntil(this.destroy$)).subscribe(([stateCode, nraIndicator]) => {
      if (stateCode && !nraIndicator) {
        this.stateTaxLabel = `${this.labels.stateTax} (${stateCode})`;
      }
    });
  }

  onEditClicked(): void {
    this.store.dispatch(ReviewPageActions.editClicked());
  }

  getPayeeName(transactionDetails: CheckTransactionDetails, isFirstPartyModified: boolean, checkInfo: CheckInfo): string[] {
    let payees: string[] = [];
    if (this.isTransactionViewPage || this.isStandingAuthViewPage) {
      payees.push(checkInfo.payeeDetails.payeeName1);
      payees.push(checkInfo.payeeDetails.payeeName2);
      payees.push(checkInfo.payeeDetails.payeeName3);
    }
    else if (isFirstPartyModified) {
      payees.push(transactionDetails?.modifiedPayeeName1 ?? '');
      payees.push(transactionDetails?.modifiedPayeeName2 ?? '');
      payees.push(transactionDetails?.modifiedPayeeName3 ?? '');
    }
    else if (transactionDetails.payeeType === 'firstParty') {
      payees = (checkInfo.payee).filter((name: string) => !!name.trim());
    }
    else {
      if (transactionDetails.payeeName) payees.push(transactionDetails.payeeName.toUpperCase());
      if (transactionDetails.additionalPayeeName1) payees.push(transactionDetails.additionalPayeeName1.toUpperCase());
      if (transactionDetails.additionalPayeeName2) payees.push(transactionDetails.additionalPayeeName2.toUpperCase());
    }
    return payees;
  }

  getPayee(useStandingAuth: string, transactionDetails: CheckTransactionDetails, isThirdParty: boolean): CheckPayeeTypes {
    if (useStandingAuth === yesNo.no) {
      return checkPayeeDict[transactionDetails.payeeType!] as CheckPayeeTypes;
    }
    return isThirdParty ? 'Third party' : 'First party (account holder)';
  }

  getDeliveryAddress(deliveryType: string, defaultRecipient: string[] | undefined, deliveryAddress: string[], transactionDetails: CheckTransactionDetails) {
    if (deliveryType === 'firstParty' && !!defaultRecipient) {
      return defaultRecipient
    }
    return transactionDetails.country === 'US' ? deliveryAddress.concat('UNITED STATES') : deliveryAddress;
  }

  parseDeliveryMethod(method: string): string {
    let deliveryFee = this.checkFee === 0 ? 'Free' : this.currencyPipe.transform(this.checkFee);
    return `${this.sentenceCasePipe.transform(method)}—${method.toLowerCase() === 'standard' ? 'Free' : deliveryFee}`;
  }

  selectReason(code: string): string {
    let filtered = this.checkReasonMap?.find((item: CodeAndDescription) => item.code === code);
    return filtered?.description ?? '';
  }

  getAmountByAmountType(transactionDetails: CheckTransactionDetails, totalWithdrawalAmount: string): string {
    if (transactionDetails.amountType === (amountTypeDict['net'] as AmountType)) {
      return (totalWithdrawalAmount ?? Constants.zeroAmount);
    }

    if (transactionDetails.amount === '') {
      return Constants.zeroAmount;
    }

    return transactionDetails.amount ?? Constants.zeroAmount;
  }

  getFrequency(extraPrompts: ChecksExtraPrompts): string{
    if (extraPrompts.useStandingAuth === 'bundled'){
      return extraPrompts.useBundledStandingAuth === 'recurring'
        ? frequencyDict.recurring
        : extraPrompts.useBundledStandingAuth === 'no'
        ? frequencyDict.onetime
        : '';
    } 
    return frequencyDict[extraPrompts.frequency!] as FrequencyTypes
  }

  getTransactionTypeLabel(extraPrompts: ChecksExtraPrompts): string {
    if(extraPrompts.useStandingAuth === 'bundled'){
    return `${transactionTypesDict[transactionTypes.check]} - bundled`;
     }
     return transactionTypesDict[transactionTypes.check]
  }
  isNotEmptyOrNull(param: any) {
    const params = param?.trim();
    if (params === null || params === undefined || params === 'undefined%' || params === '') {
      return false;
    }
    return true;
  }

  get hasCheckNumberForCompletedTransaction() : boolean {
        return this.isTransactionViewPage && this.transactionStatus.toLowerCase() === 'complete' && this.checkNumber !== '' && this.checkNumber !== '0';
  }

  formatReportsRequestedFields(form: TransactionForm) {
    if (form.transactionType && isCheckTransactionForm(form)) {
      const fields = [
        { label: 'Tax forms through: ', value: form?.extraPrompts?.taxFormsDateInputControl },
        { label: 'Account statements through: ', value: this.dateFormatPipe.transform(form?.extraPrompts?.accountStatementsDateInputControl ?? '') },
        { label: 'Trade confirmations through: ', value: this.dateFormatPipe.transform(form?.extraPrompts?.tradeConfirmationsDateInputControl ?? '') },
      ];
      fields.forEach(field => {
        if (field.value) {
          this.reportsRequested.push(field.label, field.value);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  get pageHeader(): string {
    return this.isStandingAuthViewPage ? 'Standing authorization details' : 'Transaction details';
  }

  get standingAuthDetailsLines(): string[] {
    if (!this.standingAuthDetails) {
      return [];
    }
    return [
      this.standingAuthDetails.nickname,
      ...this.standingAuthDetails.payeeLines,
      ...this.standingAuthDetails.addressLines
    ].filter(line => !!line?.trim());
  }

  resolveStandingAuthNickname(incomingNickname?: string): string {
    const normalizedIncomingNickname = incomingNickname?.trim() ?? '';
    if (normalizedIncomingNickname) {
      this.standingAuthNicknameInstructionId = this.instructionId ?? '';
      return normalizedIncomingNickname;
    }

    if (this.standingAuthNicknameInstructionId === (this.instructionId ?? '')) {
      return this.standingAuthDetails?.nickname ?? '';
    }

    return '';
  }
}
